import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  getConfigItemsCompanyEditableList,
  updateLocalSettingsMasterConfigToggle,
  updateLocalSettingsMasterConfigToggleSuccess,
} from "../../../../store/generalSettings/action";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import SettingsPanel from "../../components/SettingsPanel";
import "../general/generalSettings.css";

// Company-facing screen listing the front-end-editable config items
// (MasterConfigItem.FlagAllowFrontEndEdit = 1) with their meaning
// (Title / Description) and the company's own value + user-override toggle.
// Saves through the existing configcompany/append endpoint (reused action).
export default function ConfigItems() {
  const dispatch = useDispatch();

  const allowedSearchFilters = [
    "MasterConfigItem_Id",
    "MasterConfigItem_Key",
    "MasterConfigItem_Title",
    "MasterConfigItem_Description",
    "ConfigCompany_Value",
  ];
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemToEdit, setItemToEdit] = useState({});

  const { configItemsCompanyEditableListAPI, configItemsCompanyEditableLoading } =
    useSelector((state) => ({
      configItemsCompanyEditableListAPI:
        state.GeneralSettings.configItemsCompanyEditableList
          ?.masterconfigitem_list,
      configItemsCompanyEditableLoading:
        state.GeneralSettings.configItemsCompanyEditableListLoading,
    }));

  useEffect(() => {
    dispatch(getConfigItemsCompanyEditableList());
  }, []);

  const handleSave = (type, item, change) => {
    // configcompany/append upserts ValueVarChar (+ optional user-override flag)
    const dataUpdate = [
      {
        MasterConfigItem_Id: item?.MasterConfigItem_Id,
        ConfigCompany_FlagAllowUserOverride:
          type === "toggle"
            ? item?.ConfigCompany_FlagAllowUserOverride === "1"
              ? "0"
              : "1"
            : item?.ConfigCompany_FlagAllowUserOverride,
        ConfigCompany_Value:
          type === "companyValue" ? change : item?.ConfigCompany_Value,
      },
    ];
    dispatch(updateLocalSettingsMasterConfigToggle(dataUpdate));
  };

  const columns = useMemo(
    () => [
      {
        Header: "Item",
        accessor: "MasterConfigItem_Title",
        filterable: false,
        Cell: (card) => (
          <div>
            <span className="fw-medium">
              {card.row.original?.MasterConfigItem_Title ||
                card.row.original?.MasterConfigItem_Key}
            </span>
            <div className="text-muted fs-12">
              #{card.row.original?.MasterConfigItem_Id} ·{" "}
              {card.row.original?.MasterConfigItem_Key}
            </div>
          </div>
        ),
      },
      {
        Header: "Meaning",
        accessor: "MasterConfigItem_Description",
        filterable: false,
        Cell: (card) => (
          <span className="text-muted">
            {card.row.original?.MasterConfigItem_Description || "-"}
          </span>
        ),
      },
      {
        Header: "Your Value",
        accessor: "ConfigCompany_Value",
        filterable: false,
        Cell: (card) => (
          <div>
            {card.row.original?.MasterConfigItem_Id ===
            itemToEdit.MasterConfigItem_Id ? (
              <Input
                autoFocus
                type="text"
                className="form-control"
                placeholder="Enter value"
                value={itemToEdit.ConfigCompany_Value || ""}
                onChange={(e) =>
                  setItemToEdit({
                    ...itemToEdit,
                    ConfigCompany_Value: e.target.value,
                  })
                }
                onBlur={(e) =>
                  handleSave("companyValue", card.row.original, e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSave(
                      "companyValue",
                      card.row.original,
                      e.target.value
                    );
                  }
                }}
              />
            ) : (
              <div>
                {card.row.original?.ConfigCompany_Value || (
                  <span className="text-muted">
                    default: {card.row.original?.MasterConfigItem_DefaultValue}
                  </span>
                )}
                <i
                  title="Click to edit"
                  className="ri-edit-line edit-icon cursor-pointer text-warning ms-2"
                  onClick={() => setItemToEdit(card.row.original)}
                ></i>
              </div>
            )}
          </div>
        ),
      },
      {
        Header: "Allow User Override",
        accessor: "ConfigCompany_FlagAllowUserOverride",
        filterable: false,
        Cell: (card) => (
          <label className="switch">
            <input
              type="checkbox"
              checked={
                card.row.original?.ConfigCompany_FlagAllowUserOverride === "1"
              }
              onChange={() => handleSave("toggle", card.row.original)}
            />
            <span className="slider round"></span>
          </label>
        ),
      },
    ],
    [itemToEdit]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const filtered = (configItemsCompanyEditableListAPI || [])?.filter(
        (item) =>
          allowedSearchFilters.some(
            (key) =>
              item[key] &&
              item[key]
                .toString()
                .toLowerCase()
                .includes(searchQuery?.toLowerCase())
          )
      );
      setSearchResults(filtered);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, configItemsCompanyEditableListAPI]);

  const { updateSuccessAPI } = useSelector((state) => ({
    updateSuccessAPI:
      state.GeneralSettings.updateLocalSettingsMasterConfigToggleSuccess,
  }));

  useEffect(() => {
    if (isEmpty(updateSuccessAPI)) return;
    if (updateSuccessAPI.status === 200) {
      dispatch(getConfigItemsCompanyEditableList());
      toast.success(updateSuccessAPI.message);
      setItemToEdit({});
    } else {
      toast.error(updateSuccessAPI.message);
    }
    dispatch(updateLocalSettingsMasterConfigToggleSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccessAPI]);

  return (
    <SettingsPanel
      title="Config Items"
      description="Configurable items available to your company, what each one means, and the value you have set. Blank values fall back to the platform default."
    >
      <Row className="mt-2">
        <Col lg={12}>
          <Card id="configItemsList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <Col sm={4}>
                  <div className="search-box">
                    <Input
                      type="search"
                      className="form-control search"
                      placeholder="Search config items..."
                      onChange={(e) => setSearchQuery(e.target.value)}
                      value={searchQuery}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              {configItemsCompanyEditableLoading ? (
                <LoadingSpinner />
              ) : !isEmpty(searchResults) ? (
                <TableContainer
                  columns={columns}
                  data={searchResults || []}
                  isGlobalFilter={false}
                  isAddUserList={false}
                  customPageSize={10}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-0"
                  tableClass="align-middle table-nowrap"
                  theadClass="text-muted"
                  isLeadsFilter={false}
                />
              ) : (
                <div>There are no configurable items available.</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </SettingsPanel>
  );
}
